/**
 * Single source of truth for API URLs.
 * Production: same-origin /api/... (Cloudflare Pages function).
 * Development: optional REACT_APP_BACKEND_URL prefix.
 */

/**
 * REACT_APP_BACKEND_URL only applies in local development. In production the
 * session cookie belongs to the Pages origin, so a direct Railway URL arrives
 * without it (401/403). A stray value in the Pages build env must not break auth.
 */
export function resolveBackendOrigin(env = process.env) {
  if (env.NODE_ENV !== "development") return "";
  return (env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}

const BACKEND_ORIGIN = resolveBackendOrigin({
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
});

export function getBackendOrigin() {
  return BACKEND_ORIGIN;
}

/**
 * @param {string} path - e.g. "/business-anatomy/MSFT" or "business-anatomy/MSFT"
 * @returns {string} Absolute or root-relative API URL
 */
export function apiUrl(path) {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const apiPath = normalized.startsWith("/api/") ? normalized : `/api${normalized}`;

  if (apiPath.includes("undefined")) {
    throw new Error(`Invalid API path contains "undefined": ${apiPath}`);
  }

  if (BACKEND_ORIGIN) {
    return `${BACKEND_ORIGIN}${apiPath}`;
  }

  if (process.env.NODE_ENV === "development" && !process.env.REACT_APP_BACKEND_URL) {
    // Same-origin /api still works when CRA proxy is configured; warn once in dev console.
    if (typeof window !== "undefined" && !window.__ROOH_API_URL_WARNED) {
      window.__ROOH_API_URL_WARNED = true;
      console.info("[apiUrl] Using same-origin", apiPath, "(set REACT_APP_BACKEND_URL for direct backend)");
    }
  }

  return apiPath;
}

/** @returns {boolean} */
export function isValidApiUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (url.includes("undefined")) return false;
  if (url.startsWith("undefined")) return false;
  return url.includes("/api/");
}

export { BACKEND_ORIGIN as BACKEND_URL };
